import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { ProblemDetailsAlert } from "@/components/ProblemDetailsAlert";
import { useApiAction } from "@/hooks/useApiAction";
import { cn } from "@/lib/utils";

type OrganisationRole = components["schemas"]["OrganisationRole"];
type OrganisationMembershipResponse =
  components["schemas"]["OrganisationMembershipResponse"];

const ROLE_OPTIONS: OrganisationRole[] = ["Member", "Admin", "Owner"];

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type AddOrganisationMembershipDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: (membership: OrganisationMembershipResponse) => void;
} & (
  | {
      // From an organisation page: the organisation is fixed, the admin enters a user ID.
      organisationId: string;
      organisationName?: string;
      userId?: undefined;
    }
  | {
      // From a user page: the user is fixed, the admin enters an organisation ID.
      userId: string;
      userLabel?: string;
      organisationId?: undefined;
    }
);

export function AddOrganisationMembershipDialog(
  props: AddOrganisationMembershipDialogProps,
) {
  const { open, onOpenChange, onAdded } = props;
  const enteringUser = props.organisationId !== undefined;

  const [enteredId, setEnteredId] = useState("");
  const [role, setRole] = useState<OrganisationRole>("Member");

  const action = useApiAction(
    (
      userId: string,
      organisationId: string,
      nextRole: OrganisationRole,
    ): Promise<{
      data?: OrganisationMembershipResponse;
      error?: components["schemas"]["ProblemDetails"];
    }> =>
      api.POST("/admin/users/{userId}/organisations/{organisationId}", {
        params: { path: { userId, organisationId } },
        body: { role: nextRole },
      }),
  );

  useEffect(() => {
    if (open) {
      setEnteredId("");
      setRole("Member");
      action.reset();
    }
    // intentionally only re-run on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmedId = enteredId.trim();
  const idValid = GUID_PATTERN.test(trimmedId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!idValid) return;
    const userId = enteringUser ? trimmedId : props.userId!;
    const organisationId = enteringUser ? props.organisationId! : trimmedId;
    const result = await action.run(userId, organisationId, role);
    if (result) {
      onAdded(result);
      onOpenChange(false);
    }
  }

  const target = enteringUser
    ? (props.organisationName ?? "this organisation")
    : (props.userLabel ?? "this user");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{enteringUser ? "Add user" : "Add organisation"}</DialogTitle>
            <DialogDescription>
              {enteringUser ? (
                <>
                  Add an existing user to{" "}
                  <span className="text-foreground font-medium">{target}</span>.
                </>
              ) : (
                <>
                  Add{" "}
                  <span className="text-foreground font-medium">{target}</span>{" "}
                  to an existing organisation.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="membership-id">
                {enteringUser ? "User ID" : "Organisation ID"}
              </Label>
              <Input
                id="membership-id"
                value={enteredId}
                onChange={(e) => setEnteredId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                className="font-mono text-sm"
                autoFocus
                disabled={action.pending}
              />
              {trimmedId && !idValid && (
                <p className="text-destructive text-xs">Enter a valid ID (GUID).</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <div className="flex flex-wrap gap-2">
                {ROLE_OPTIONS.map((r) => {
                  const selected = role === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRole(r)}
                      disabled={action.pending}
                      aria-pressed={selected}
                      className="rounded-full focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Badge
                        variant={selected ? "default" : "outline"}
                        className={cn(
                          "cursor-pointer px-3 py-1 text-xs",
                          !selected && "hover:bg-accent",
                        )}
                      >
                        {r}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            </div>

            <ProblemDetailsAlert
              problem={action.error}
              fallback={enteringUser ? "Failed to add user." : "Failed to add organisation."}
            />
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={action.pending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={action.pending || !idValid}>
              {action.pending && <Spinner className="size-4" />}
              Add
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
